import { SlidersHorizontal } from 'lucide-react';
import React, { useState } from 'react';

import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
    DrawerTrigger,
} from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

import { activeFilterCount, NO_FILTER, type TapeFilter } from './filters';
import { TapeFilterBar } from './TapeFilterBar';

/**
 * The tape's filters, on a phone.
 *
 * Inline they wrapped to four rows and pushed the first trade a third of the
 * way down a page whose whole job is the feed. Behind a button they cost one
 * control, and the sheet gives each filter a name and room to be tapped —
 * better than the wrapped strip was even before it was too tall.
 *
 * The same bottom-sheet idiom the terminal already uses for its market picker
 * and positions, so a phone user meets one pattern rather than three.
 *
 * Filtering applies as each chip is tapped; the sheet is not a form and has no
 * Apply. The footer's count is what makes that legible — the feed is changing
 * behind the overlay, and "Show 12 trades" says what dismissing will reveal.
 */
export const TapeFilterSheet: React.FC<{
    filter: TapeFilter;
    onChange: (next: TapeFilter) => void;
    coins: readonly string[];
    /** rows that survive the current filter, for the dismiss button */
    matching: number;
}> = ({ filter, onChange, coins, matching }) => {
    const [open, setOpen] = useState(false);
    const active = activeFilterCount(filter);

    return (
        <Drawer open={open} onOpenChange={setOpen}>
            <DrawerTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex h-7 shrink-0 items-center gap-1.5 rounded-full px-2.5 font-mono text-[11px] font-semibold transition-colors',
                        active > 0
                            ? 'bg-[rgba(236,238,228,0.1)] text-foreground'
                            : 'text-muted-foreground hover:bg-accent/40',
                    )}
                >
                    <SlidersHorizontal size={12} />
                    Filters
                    {/* A shut sheet hides its own state; without this a viewer
                        cannot tell a quiet tape from a filtered one. */}
                    {active > 0 && (
                        <span className="rounded-full bg-primary px-1.5 font-mono text-[9px] font-bold text-primary-foreground">
                            {active}
                        </span>
                    )}
                </button>
            </DrawerTrigger>

            <DrawerContent>
                <DrawerHeader className="flex flex-row items-center justify-between">
                    <DrawerTitle>Filter the tape</DrawerTitle>
                    <button
                        type="button"
                        onClick={() => onChange(NO_FILTER)}
                        disabled={active === 0}
                        className={cn(
                            'font-mono text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors',
                            active > 0
                                ? 'text-muted-foreground hover:text-foreground'
                                : 'pointer-events-none opacity-0',
                        )}
                    >
                        Clear
                    </button>
                </DrawerHeader>

                <div className="max-h-[60dvh] overflow-y-auto px-4 pb-2">
                    <TapeFilterBar
                        filter={filter}
                        onChange={onChange}
                        coins={coins}
                        layout="stack"
                    />
                </div>

                <div className="px-4 pb-6 pt-3">
                    <DrawerClose asChild>
                        <button
                            type="button"
                            className="h-11 w-full rounded-xl bg-primary font-sans text-sm font-bold text-primary-foreground"
                        >
                            {/* "Show 0 trades" is an invitation to dismiss the
                                sheet onto nothing. The feed behind says why it
                                is empty; this says what is true. */}
                            {matching === 0 && 'No trades match'}
                            {matching === 1 && 'Show 1 trade'}
                            {matching > 1 && `Show ${matching} trades`}
                        </button>
                    </DrawerClose>
                </div>
            </DrawerContent>
        </Drawer>
    );
};

export default TapeFilterSheet;
